import axios, { AxiosHeaders, isAxiosError } from "axios";
import { useImpersonationStore } from "@/stores/user-impersonation";
import HttpError from "./httpError";

const isObject = (value: unknown): value is object => {
  return typeof value === "object" && value !== null;
};

const request = async <T>({
  method,
  path,
  params,
  body,
  token,
  signal,
  contentType = "application/json",
}: {
  method: "GET" | "POST" | "PATCH" | "PUT" | "DELETE";
  path: `/${string}`;
  params?: Record<string, unknown>;
  body?: BodyInit;
  token?: string;
  signal?: AbortSignal;
  contentType?:
    | "application/json"
    | "multipart/form-data"
    | "application/x-www-form-urlencoded";
}): Promise<T> => {
  try {
    const headers = new AxiosHeaders();
    headers.setContentType(contentType);

    if (token) {
      headers.setAuthorization(`Bearer ${token}`);
    }

    const impersonatedUserId =
      useImpersonationStore.getState().impersonatedUserId;
    if (impersonatedUserId) {
      headers.set("x-impersonate-user-id", impersonatedUserId);
    }

    const response = await axios.request<T>({
      method,
      url: `${path}`,
      headers,
      signal,
      data: body,
      params,
    });

    return response.data;
  } catch (error) {
    if (isAxiosError(error)) {
      const errorData = error?.response?.data as unknown;

      let message = "Request failed";
      let errorType: string | undefined = undefined;
      if (isObject(errorData)) {
        if ("error" in errorData && typeof errorData.error === "string") {
          errorType = errorData.error;
        }

        if ("message" in errorData && typeof errorData.message === "string") {
          message = errorData.message;
        }
      }

      throw new HttpError({
        status: error?.response?.status,
        message,
        errorType,
        body: error,
      });
    } else if (error instanceof Error) {
      throw new HttpError({
        message: error.message ?? "Request failed",
        body: error,
      });
    } else {
      throw new HttpError({
        message: `An unexpected error occurred for ${method} ${path}`,
        body: error,
      });
    }
  }
};

export default request;
